/* cspell:ignore cuda nics storageclass */
import { get } from 'lodash';

import type { NicConfigurationItem } from '@/types/nic-configuration';

import type { VolumeItem } from '../../../capabilities/container-storage';
import { VolumeMountTab, VolumeMountType, isVolumeClaimTemplateItem, isVolumeStorageItem } from '../../../capabilities/container-storage';
import type { MaasEnvironment, MaasGroupSpec, MaasNetwork, MaasResourceStorage } from '../../../capabilities/maas-init/maasTypes';
import type { ModelInstanceSpec } from '../../../capabilities/maas-model-storage/modelInstanceTypes';
import { buildMaasWorkloadRequestBody } from '../../../capabilities/maas-node-groups';
import { parseGiSize } from '../../../utils/diskSize';

/** 训练集群提交的网卡配置。 */
export type TrainingClusterNic = NicConfigurationItem;

/** 训练集群网络配置。 */
export interface TrainingClusterNetwork extends MaasNetwork {
  nics?: TrainingClusterNic[];
}

/** 与训练集群创建接口一致的 deliveryMetadata。 */
export interface TrainingClusterSpec {
  alias?: string;
  creator: string;
  datacenter?: string;
  /** 实例级副本数；训练集群固定为 1，UI 不展示但需提交 API */
  replicas?: number;
  description?: string;
  environment?: MaasEnvironment;
  headGroupSpec?: MaasGroupSpec;
  initCommand?: ModelInstanceSpec['initCommand'];
  network?: TrainingClusterNetwork;
  runtime?: Pick<NonNullable<ModelInstanceSpec['runtime']>, 'type'>;
  /** 工作负载级共享 Data 卷。 */
  storage?: MaasResourceStorage;
  workerGroupSpecs?: MaasGroupSpec[];
  workspace?: string;
}

function readRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
}

function sanitizeTrainingClusterNics(nics: unknown): TrainingClusterNic[] | undefined {
  if (!Array.isArray(nics)) return undefined;
  const result = nics.flatMap((rawNic): TrainingClusterNic[] => {
    const nic = readRecord(rawNic);
    if (!nic) return [];
    const resource = Object.fromEntries(
      Object.entries(readRecord(nic.resource) || {}).flatMap(([resourceName, rawResource]) => {
        const item = readRecord(rawResource);
        const cidrBlocks = Array.isArray(item?.cidrBlocks)
          ? [
              ...new Set(
                item.cidrBlocks.filter((cidr): cidr is string => typeof cidr === 'string' && !!cidr.trim()).map((cidr) => cidr.trim()),
              ),
            ]
          : [];
        if (!resourceName || !cidrBlocks.length) return [];
        const quantity = readRecord(item?.quantity);
        return [
          [
            resourceName,
            {
              quantity: {
                limit: String(quantity?.limit || '1'),
                request: String(quantity?.request || '1'),
              },
              ...(item?.networkAttachmentDefine ? { networkAttachmentDefine: String(item.networkAttachmentDefine) } : {}),
              cidrBlocks,
            },
          ],
        ];
      }),
    );
    if (!String(nic.resourceType || '').trim() || !Object.keys(resource).length) return [];
    return [
      {
        vendor: String(nic.vendor || ''),
        vendorID: String(nic.vendorID || ''),
        device: String(nic.device || ''),
        deviceID: String(nic.deviceID || ''),
        resourceType: String(nic.resourceType),
        ...(nic.linkSpeed ? { linkSpeed: String(nic.linkSpeed) } : {}),
        ...(nic.linkType ? { linkType: String(nic.linkType) } : {}),
        resource,
      },
    ];
  });
  return result.length ? result : undefined;
}

function sanitizeTrainingClusterNetwork(network: unknown, nics?: TrainingClusterNic[]): TrainingClusterNetwork | undefined {
  const source = readRecord(network) || {};
  const sanitizedNics = sanitizeTrainingClusterNics(nics ?? source.nics);
  const result: TrainingClusterNetwork = {
    vpc: typeof source.vpc === 'string' ? source.vpc : undefined,
    securitygroup: typeof source.securitygroup === 'string' ? source.securitygroup : undefined,
    nics: sanitizedNics,
  };
  return result.vpc || result.securitygroup || result.nics?.length ? result : undefined;
}

function formatGi(size: number | undefined): string | undefined {
  return typeof size === 'number' && Number.isFinite(size) && size > 0 ? `${size}Gi` : undefined;
}

const TRAINING_VOLUME_ACCESS_MODES = new Set(['ReadWriteOnce', 'ReadOnlyMany', 'ReadWriteMany']);

function normalizeAccessModes(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const modes = value.filter((item): item is string => typeof item === 'string' && TRAINING_VOLUME_ACCESS_MODES.has(item));
  return modes.length ? modes : undefined;
}

/** 查找被不同共享存储卷重复使用的挂载路径。 */
export function findDuplicateTrainingSharedVolumeMountPath(volumes: VolumeItem[] | undefined): string | undefined {
  const usedPaths = new Set<string>();
  for (const volume of volumes || []) {
    const currentVolumePaths = new Map<string, string>();
    for (const mount of volume.volumeMounts || []) {
      const displayPath = String(mount.mountPath || '').trim();
      if (!displayPath) continue;
      const normalizedPath = displayPath.replace(/\/+$/, '') || '/';
      currentVolumePaths.set(normalizedPath, displayPath);
    }
    for (const [normalizedPath, displayPath] of currentVolumePaths) {
      if (usedPaths.has(normalizedPath)) return displayPath;
      usedPaths.add(normalizedPath);
    }
  }
  return undefined;
}

/** 将容器存储表单记录转换为训练集群节点组的共享存储卷。 */
export function buildTrainingClusterSharedVolumes(volumes: VolumeItem[] | undefined): Record<string, unknown>[] {
  if (!Array.isArray(volumes)) return [];
  return volumes.flatMap((volume): Record<string, unknown>[] => {
    const name = String(volume.volumeName || '').trim();
    const mountPath = String(volume.volumeMounts?.find((item) => item.mountPath?.trim())?.mountPath || '').trim();
    if (!name || !mountPath) return [];
    const reclaimPolicy = volume.reclaimPolicy || 'Delete';

    if (isVolumeStorageItem(volume) && volume.type === VolumeMountTab.PERSISTENCE) {
      const claimName = String(volume.selected || '').trim();
      if (!claimName) return [];
      return [
        {
          name,
          type: 'Data',
          reclaimPolicy,
          source: { type: 'PersistentVolumeClaim', mountPath, persistentVolumeClaimName: claimName },
        },
      ];
    }

    if (!isVolumeClaimTemplateItem(volume)) return [];
    const size = formatGi(volume.volumeCapacity);
    const accessModes = normalizeAccessModes(volume.accessModes) || ['ReadWriteOnce'];
    return [
      {
        name,
        type: 'Data',
        reclaimPolicy,
        source: {
          type: 'PersistentVolumeClaimTemplate',
          mountPath,
          persistentVolumeClaimTemplate: {
            accessModes,
            resources: { requests: { ...(size ? { storage: size } : {}) } },
            ...(volume.storageClass ? { storageClassName: volume.storageClass } : {}),
          },
        },
      },
    ];
  });
}

function getTrainingClusterDataVolumes(storage: MaasResourceStorage | undefined): Record<string, unknown>[] {
  return Array.isArray(storage?.volume) ? storage.volume.filter((volume) => readRecord(volume)?.type === 'Data') : [];
}

function stripTrainingClusterGroupDataVolumes(group: TrainingClusterSpec['headGroupSpec']): TrainingClusterSpec['headGroupSpec'] {
  if (!group?.resource?.storage) return group;
  const resource = { ...group.resource };
  const storage = { ...resource.storage };
  const volumes = Array.isArray(storage.volume) ? storage.volume.filter((volume) => readRecord(volume)?.type !== 'Data') : [];
  if (volumes.length) storage.volume = volumes;
  else delete storage.volume;
  if (Object.keys(storage).length) resource.storage = storage;
  else delete resource.storage;
  return { ...group, resource: Object.keys(resource).length ? resource : undefined };
}

/** 从 Head 或首个配置了镜像的 Worker 节点组读取训练镜像地址。 */
export function getTrainingClusterImage(
  head: TrainingClusterSpec['headGroupSpec'],
  workers: TrainingClusterSpec['workerGroupSpecs'],
): string {
  const groups = [head, ...(workers || [])];
  for (const group of groups) {
    const system = readRecord(group?.system);
    const image = String(system?.image || system?.url || '').trim();
    if (image) return image;
  }
  return '';
}

function attachTrainingImage(group: TrainingClusterSpec['headGroupSpec'], image: string): TrainingClusterSpec['headGroupSpec'] {
  if (!group || !image) return group;
  return { ...group, system: { ...(readRecord(group.system) || {}), image, url: image } };
}

/** 从顶层 storage 回填共享存储卷表单，缺失时返回空列表。 */
export function getTrainingClusterSharedVolumes(spec: TrainingClusterSpec): VolumeItem[] {
  const volumes = getTrainingClusterDataVolumes(spec.storage);

  return volumes.flatMap((rawVolume): VolumeItem[] => {
    const volume = readRecord(rawVolume);
    const source = readRecord(volume?.source);
    if (!volume || volume.type !== 'Data' || !source) return [];
    const pvcTemplate = readRecord(source.persistentVolumeClaimTemplate);
    const pvcResources = readRecord(pvcTemplate?.resources);
    const pvcRequests = readRecord(pvcResources?.requests);
    const storageRaw = pvcRequests?.storage ?? volume.size;
    const name = String(volume.name || '').trim();
    const mountPath = String(source.mountPath || volume.mountPath || '').trim();
    if (!name) return [];
    const accessModes = normalizeAccessModes(pvcTemplate?.accessModes ?? volume.accessModes);
    const mountType = (accessModes?.[0] as VolumeMountType | undefined) || VolumeMountType.ReadWriteMany;
    const volumeMounts = mountPath ? [{ containerName: '', mountType, mountPath }] : undefined;

    if (pvcTemplate || source.type === 'PersistentVolumeClaimTemplate') {
      return [
        {
          type: 'VolumeClaimTemplate',
          volumeName: name,
          storageClass: String(pvcTemplate?.storageClassName || volume.storageclass || volume.storageClassName || '').trim() || undefined,
          accessModes,
          ...(storageRaw ? { volumeCapacity: parseGiSize(storageRaw) } : {}),
          reclaimPolicy: String(volume.reclaimPolicy || '').trim() || undefined,
          volumeMounts,
        },
      ];
    }

    return [
      {
        type: VolumeMountTab.PERSISTENCE,
        selected: String(source.persistentVolumeClaimName || source.claimName || '').trim(),
        volumeName: name,
        volumeHostPath: '',
        reclaimPolicy: String(volume.reclaimPolicy || '').trim() || undefined,
        volumeMounts,
      },
    ];
  });
}

function sanitizeTrainingClusterGroup(group: unknown): TrainingClusterSpec['headGroupSpec'] {
  if (!group || typeof group !== 'object' || Array.isArray(group)) return undefined;
  const next = { ...(group as Record<string, unknown>) };

  if (next.system && typeof next.system === 'object' && !Array.isArray(next.system)) {
    const system = { ...(next.system as Record<string, unknown>) };
    delete system.type;
    next.system = Object.keys(system).length ? system : undefined;
  }

  if (next.resource && typeof next.resource === 'object' && !Array.isArray(next.resource)) {
    const resource = { ...(next.resource as Record<string, unknown>) };
    if (resource.cpu && typeof resource.cpu === 'object' && !Array.isArray(resource.cpu)) {
      const cpu = { ...(resource.cpu as Record<string, unknown>) };
      delete cpu.sockets;
      delete cpu.threads;
      resource.cpu = cpu;
    }
    if (resource.gpu && typeof resource.gpu === 'object' && !Array.isArray(resource.gpu)) {
      const gpu = { ...(resource.gpu as Record<string, unknown>) };
      const driverVersion = typeof gpu.driverVersion === 'string' ? gpu.driverVersion : undefined;
      const cudaVersion = typeof gpu.cudaVersion === 'string' ? gpu.cudaVersion : undefined;
      const software = Array.isArray(gpu.software)
        ? gpu.software
        : driverVersion || cudaVersion
          ? [{ ...(driverVersion ? { driverVersion } : {}), ...(cudaVersion ? { cudaVersion } : {}) }]
          : undefined;
      delete gpu.number;
      delete gpu.driverVersion;
      delete gpu.cudaVersion;
      gpu.memory = String(gpu.memory === undefined || gpu.memory === null || gpu.memory === '' ? 0 : gpu.memory);
      if (software?.length) gpu.software = software;
      else delete gpu.software;
      resource.gpu = gpu;
    }
    const sanitizedGroupNics = sanitizeTrainingClusterNics(resource.nics ?? get(resource, 'network.nics'));
    if (sanitizedGroupNics?.length) resource.nics = sanitizedGroupNics;
    else delete resource.nics;
    if (resource.network && typeof resource.network === 'object' && !Array.isArray(resource.network)) {
      const network = { ...(resource.network as Record<string, unknown>) };
      delete network.service;
      delete network.nics;
      if (Object.keys(network).length) resource.network = network;
      else delete resource.network;
    }
    next.resource = resource;
  }

  return next as unknown as TrainingClusterSpec['headGroupSpec'];
}

/** 将模型实例资源表单裁剪为训练集群创建接口结构。 */
export function buildTrainingClusterSpec(spec: ModelInstanceSpec, sharedVolumes?: VolumeItem[]): TrainingClusterSpec {
  const serialized = buildMaasWorkloadRequestBody(spec);
  const runtimeType = spec.runtime?.type;
  const dataVolumes = buildTrainingClusterSharedVolumes(sharedVolumes);
  const headGroupSpec = sanitizeTrainingClusterGroup(serialized.headGroupSpec);
  const workerGroupSpecs = Array.isArray(serialized.workerGroupSpecs)
    ? serialized.workerGroupSpecs.flatMap((group) => {
        const sanitized = sanitizeTrainingClusterGroup(group);
        return sanitized ? [sanitized] : [];
      })
    : undefined;
  const replicas = typeof serialized.replicas === 'number' && serialized.replicas >= 0 ? serialized.replicas : (spec.replicas ?? 1);
  return {
    alias: serialized.alias as string | undefined,
    creator: String(serialized.creator || ''),
    datacenter: spec.datacenter,
    replicas,
    description: (serialized.description as string | undefined) ?? '',
    environment: serialized.environment as TrainingClusterSpec['environment'],
    headGroupSpec: stripTrainingClusterGroupDataVolumes(headGroupSpec),
    initCommand: serialized.initCommand as TrainingClusterSpec['initCommand'],
    network: sanitizeTrainingClusterNetwork(serialized.network),
    runtime: runtimeType ? { type: runtimeType } : undefined,
    storage: dataVolumes.length ? { volume: dataVolumes } : undefined,
    workerGroupSpecs: workerGroupSpecs?.map((group) => stripTrainingClusterGroupDataVolumes(group)!),
    workspace: serialized.workspace as string | undefined,
  };
}
